<template>
  <div class="painel-nao-resolvidos" v-if="termos.length > 0 || loading">
    <div class="painel-header" @click="expanded = !expanded">
      <div class="painel-title">
        <span class="badge-count">{{ termos.length }}</span>
        <strong>Termos Não Resolvidos</strong>
        <small class="text-muted">nos últimos {{ dias }} dias</small>
      </div>
      <CIcon :name="expanded ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
    </div>

    <div v-if="expanded" class="painel-body">
      <div class="painel-filters">
        <select class="form-control form-control-sm" v-model.number="dias" @change="load">
          <option :value="7">Últimos 7 dias</option>
          <option :value="30">Últimos 30 dias</option>
          <option :value="90">Últimos 90 dias</option>
        </select>
      </div>

      <div v-if="loading" class="text-center py-3">
        <CSpinner size="sm" color="primary" />
      </div>

      <div v-else class="termos-list">
        <div
          v-for="termo in termos"
          :key="termo.id"
          class="termo-row"
        >
          <div class="termo-info">
            <span class="termo-text">{{ termo.termo_original }}</span>
            <span class="freq-badge" :title="`Buscado ${termo.frequencia}x`">
              {{ termo.frequencia }}x
            </span>
          </div>
          <div class="termo-actions">
            <CButton
              size="sm"
              color="primary"
              variant="outline"
              :disabled="termo._loading"
              @click="sugerirMapeamento(termo)"
            >
              {{ termo._loading ? '...' : 'Mapear' }}
            </CButton>
            <CButton
              size="sm"
              color="secondary"
              variant="ghost"
              @click="descartar(termo)"
            >
              <CIcon name="cil-x" />
            </CButton>
          </div>

          <!-- Sugestão expandida -->
          <div v-if="termo._sugestao" class="sugestao-box">
            <div v-if="termo._sugestao.produto_nome" class="sugestao-match">
              <span class="sugestao-label">Produto sugerido:</span>
              <strong>{{ termo._sugestao.produto_nome }}</strong>
              <span
                class="confianca-badge"
                :class="'confianca-' + termo._sugestao.confianca"
              >
                {{ termo._sugestao.confianca }}
              </span>
            </div>
            <div v-else class="sugestao-match text-muted">
              Nenhum produto encontrado automaticamente.
            </div>

            <div v-if="termo._sugestao.aliases_sugeridos && termo._sugestao.aliases_sugeridos.length" class="sugestao-aliases">
              <small class="text-muted">Aliases sugeridos:</small>
              <span
                v-for="alias in termo._sugestao.aliases_sugeridos"
                :key="alias"
                class="badge-alias"
              >{{ alias }}</span>
            </div>

            <!-- Semelhantes encontrados -->
            <div
              v-if="termo._semelhantes && termo._semelhantes.length"
              class="semelhantes-hint"
            >
              <div class="semelhantes-title">
                <CIcon name="cil-info" size="sm" />
                <span>Termos semelhantes já cadastrados</span>
              </div>
              <div
                v-for="(s, idx) in termo._semelhantes"
                :key="idx"
                class="semelhante-item"
              >
                <span class="semelhante-texto">"{{ s.termo }}" &rarr; "{{ s.termo_canonico }}"</span>
                <span
                  class="semelhante-badge"
                  :class="s.match_em === 'alias' ? 'badge-alias' : 'badge-canonico'"
                >
                  {{ s.match_em === 'alias' ? 'alias' : 'canônico' }}
                </span>
                <span class="semelhante-pct">{{ Math.round(s.similaridade * 100) }}%</span>
              </div>
            </div>

            <div class="sugestao-actions">
              <input
                type="text"
                class="form-control form-control-sm"
                v-model="termo._termoCanonicoInput"
                :placeholder="termo._sugestao.produto_nome || 'Nome oficial do produto...'"
                @input="buscarSemelhantesParaTermo(termo)"
              />
              <CButton
                size="sm"
                color="primary"
                :disabled="!getTermoCanonicoFinal(termo)"
                @click="confirmarMapeamento(termo)"
              >
                Confirmar
              </CButton>
              <CButton
                size="sm"
                color="secondary"
                variant="ghost"
                @click="termo._sugestao = null"
              >
                Cancelar
              </CButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post, del } from "@/helpers/apiRequest";

let semelhantesTimeout;

export default {
  name: "painel-termos-nao-resolvidos",
  data() {
    return {
      termos: [],
      loading: false,
      expanded: false,
      dias: 30,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const resp = await get(
          `/admin/dicionario-ia/nao-resolvidos?dias=${this.dias}&take=50`
        );
        this.termos = (resp.data || resp || []).map((t) => ({
          ...t,
          _loading: false,
          _sugestao: null,
          _termoCanonicoInput: "",
          _semelhantes: [],
          _buscandoSemelhantes: false,
        }));
      } catch (e) {
        console.error("Erro ao carregar termos:", e);
      }
      this.loading = false;
    },
    async sugerirMapeamento(termo) {
      termo._loading = true;
      try {
        const resp = await post(
          `/admin/dicionario-ia/nao-resolvidos/${termo.id}/sugerir`
        );
        termo._sugestao = resp.data || resp;
        termo._termoCanonicoInput = termo._sugestao.produto_nome || "";
        if (termo._termoCanonicoInput.length >= 3) {
          this.buscarSemelhantesParaTermo(termo);
        }
      } catch (e) {
        console.error("Erro ao sugerir:", e);
        this.$swal({
          title: "Erro ao gerar sugestão",
          text: e.message || "Verifique a configuração da IA.",
          icon: "error",
        });
      }
      termo._loading = false;
    },
    getTermoCanonicoFinal(termo) {
      return (
        termo._termoCanonicoInput?.trim() ||
        termo._sugestao?.produto_nome ||
        ""
      ).trim();
    },
    async confirmarMapeamento(termo) {
      const termoCanonicoFinal = this.getTermoCanonicoFinal(termo);
      if (!termoCanonicoFinal) return;

      try {
        await post(`/admin/dicionario-ia/nao-resolvidos/${termo.id}/mapear`, {
          termo_canonico: termoCanonicoFinal,
          tipo: "produto",
        });

        // Se houver aliases sugeridos, salvá-los também em lote
        const aliasesSugeridos = termo._sugestao?.aliases_sugeridos || [];
        if (aliasesSugeridos.length > 0) {
          const termos = aliasesSugeridos.map((alias) => ({
            termo: alias,
            termo_canonico: termoCanonicoFinal,
            tipo: "produto",
          }));
          await post("/admin/dicionario-ia/treino/salvar-lote", { termos });
        }

        this.termos = this.termos.filter((t) => t.id !== termo.id);
        this.$emit("updated");
      } catch (e) {
        console.error("Erro ao mapear:", e);
      }
    },
    buscarSemelhantesParaTermo(termo) {
      clearTimeout(semelhantesTimeout);
      const canonico = (termo._termoCanonicoInput || "").trim();

      if (canonico.length < 3) {
        this.$set(termo, "_semelhantes", []);
        return;
      }

      this.$set(termo, "_buscandoSemelhantes", true);
      semelhantesTimeout = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          params.set("canonico", canonico);
          const resp = await get(
            `/admin/dicionario-ia/semelhantes?${params.toString()}`
          );
          const data = resp.data || resp || [];
          this.$set(termo, "_semelhantes", data);
        } catch (e) {
          console.error("Erro ao buscar semelhantes:", e);
          this.$set(termo, "_semelhantes", []);
        } finally {
          this.$set(termo, "_buscandoSemelhantes", false);
        }
      }, 500);
    },
    async descartar(termo) {
      try {
        await del(`/admin/dicionario-ia/nao-resolvidos/${termo.id}`);
        this.termos = this.termos.filter((t) => t.id !== termo.id);
      } catch (e) {
        console.error("Erro ao descartar:", e);
      }
    },
  },
};
</script>

<style scoped>
.painel-nao-resolvidos {
  border: 1px solid #fce4ec;
  border-radius: 10px;
  margin-bottom: 20px;
  overflow: hidden;
}

.painel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff5f5;
  cursor: pointer;
  user-select: none;
}

.painel-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  border-radius: 12px;
  background: #c62828;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 0 6px;
}

.painel-body {
  padding: 12px 16px;
}

.painel-filters {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.painel-filters select {
  width: auto;
  min-width: 160px;
}

.termos-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.termo-row {
  padding: 10px 12px;
  border: 1px solid #f2f4f7;
  border-radius: 8px;
}

.termo-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.termo-text {
  font-weight: 500;
  font-size: 14px;
}

.freq-badge {
  background: #fce4ec;
  color: #c62828;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.termo-actions {
  display: flex;
  gap: 6px;
}

/* Sugestão */
.sugestao-box {
  margin-top: 10px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e4e7eb;
}

.sugestao-match {
  margin-bottom: 8px;
  font-size: 13px;
}

.sugestao-label {
  color: #667085;
  margin-right: 4px;
}

.confianca-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 6px;
}

.confianca-alta {
  background: #e8f5e9;
  color: #2e7d32;
}

.confianca-media {
  background: #fff3e0;
  color: #e65100;
}

.confianca-baixa {
  background: #fce4ec;
  color: #c62828;
}

.sugestao-aliases {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  margin-bottom: 10px;
}

.badge-alias {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  background: #f2f2f8;
  color: #3a3e81;
}

.semelhantes-hint {
  margin: 0 0 10px;
  background: #f2f2f8;
  border: 1px solid #dddce9;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
}

.semelhantes-title {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3a3e81;
  font-weight: 600;
  margin-bottom: 7px;
}

.semelhante-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  color: #374151;
  border-bottom: 1px dashed #dddce9;
}

.semelhante-item:last-child {
  border-bottom: none;
}

.semelhante-texto {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.semelhante-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}

.badge-alias {
  background: #edf8f9;
  color: #2298a3;
  border: 1px solid #c5e6e9;
}

.badge-canonico {
  background: #f2f2f8;
  color: #3a3e81;
  border: 1px solid #dddce9;
}

.semelhante-pct {
  color: #9ca3af;
  font-size: 11px;
  flex-shrink: 0;
}

.sugestao-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.sugestao-actions input {
  flex: 1;
}
</style>
